"use client";

import { Shield } from "lucide-react";
import { useTranslations } from "next-intl";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { cn } from "@/lib/utils";

export interface PartnerSuggestion {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  verified: boolean;
}

interface SearchPartnerSuggestionsProps {
  partners: PartnerSuggestion[];
  searchText: string;
  /** Called with the partner slug when a row is clicked. */
  onSelect: (slug: string) => void;
  /** Per-row button className (mobile vs desktop spacing differs). */
  itemClassName?: string;
  /** Max rows to render. */
  limit?: number;
}

/**
 * 검색 자동완성 드롭다운 최상단의 파트너(업체) 제안 목록.
 * 각 행: 둥근 사각형 로고 + (이름 위) "파트너" eyebrow 태그 + 파트너명.
 * 모바일/데스크탑 드롭다운에서 공용으로 사용한다.
 */
export default function SearchPartnerSuggestions({
  partners,
  searchText,
  onSelect,
  itemClassName,
  limit = 5,
}: SearchPartnerSuggestionsProps) {
  const t = useTranslations("search");

  if (partners.length === 0) return null;

  // 검색어와 일치하는 부분을 강조 (SearchBar.highlightText 와 동일 동작)
  const highlight = (text: string) => {
    if (!searchText || searchText.length < 2) return text;
    const regex = new RegExp(
      `(${searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    return text.split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="text-orange-500 font-semibold">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <div>
      {partners.slice(0, limit).map((partner) => (
        <button
          key={partner.id}
          onClick={() => onSelect(partner.slug)}
          className={cn(
            "w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 transition",
            itemClassName,
          )}
        >
          {/* 로고 — 둥근 사각형. 없으면 그라데이션 + 첫글자 fallback */}
          {partner.logo_url ? (
            <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-white shrink-0">
              <OptimizedImage
                src={partner.logo_url}
                alt={partner.name}
                width={40}
                height={40}
                className="w-full h-full object-contain"
                sizes="40px"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-linear-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-base shrink-0">
              {partner.name?.[0]?.toUpperCase() || "P"}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-xs text-orange-500 font-medium leading-tight">
              {t("partnerLabel")}
            </p>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-900 truncate">
                {highlight(partner.name)}
              </span>
              {partner.verified && (
                <Shield className="w-3.5 h-3.5 text-orange-500 shrink-0" />
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
